/**
 * MapSettings - Modal for configuring heatmap/map options
 * Base layer, zoom, fullscreen, layer toggles
 */

import { useState } from 'react';
import { TILE_URLS } from '@/config/mapTiles';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

export interface MapConfig {
  baseLayer: string;
  zoomControl: boolean;
  fullscreenControl: boolean;
  showLayersControl: boolean;
  showLabels: boolean;
  showInfoOnHover: boolean;
  showHeatMarkers: boolean;
  heatmapStyle: 'bubbles' | 'fill';
  showKpiBadges: boolean;
  showLegend: boolean;
  markerOpacity: number;
}

export const DEFAULT_MAP_CONFIG: MapConfig = {
  baseLayer: 'google-roadmap',
  zoomControl: true,
  fullscreenControl: false,
  showLayersControl: true,
  showLabels: true,
  showInfoOnHover: true,
  showHeatMarkers: true,
  heatmapStyle: 'fill',
  showKpiBadges: true,
  showLegend: true,
  markerOpacity: 80,
};

export const BASE_LAYERS: { key: string; label: string; preview: string; url: string; attribution: string; maxZoom?: number }[] = [
  {
    key: 'osm',
    label: 'OpenStreetMap',
    preview: 'ri-map-line',
    url: TILE_URLS.osm,
    attribution: '&copy; OSM',
    maxZoom: 19,
  },
  {
    key: 'google-roadmap',
    label: 'Google Roads',
    preview: 'ri-road-map-line',
    url: TILE_URLS.googleRoads,
    attribution: '&copy; Google',
    maxZoom: 20,
  },
  {
    key: 'google-satellite',
    label: 'Google Satellite',
    preview: 'ri-earth-line',
    url: TILE_URLS.googleSatellite,
    attribution: '&copy; Google',
    maxZoom: 20,
  },
  {
    key: 'google-hybrid',
    label: 'Google Hybrid',
    preview: 'ri-image-line',
    url: TILE_URLS.googleHybrid,
    attribution: '&copy; Google',
    maxZoom: 20,
  },
  {
    key: 'google-terrain',
    label: 'Google Terrain',
    preview: 'ri-landscape-line',
    url: TILE_URLS.googleTerrain,
    attribution: '&copy; Google',
    maxZoom: 20,
  },
];

interface MapSettingsProps {
  isOpen: boolean;
  onClose: () => void;
  config: MapConfig;
  onChange: (config: MapConfig) => void;
}

export default function MapSettings({ isOpen, onClose, config, onChange }: MapSettingsProps) {
  const [localConfig, setLocalConfig] = useState<MapConfig>(config);

  const update = (partial: Partial<MapConfig>) => {
    const next = { ...localConfig, ...partial };
    setLocalConfig(next);
    onChange(next);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div className="flex items-start justify-end min-h-screen pt-16 pb-20 pr-4">
        <div className="fixed inset-0 bg-black/40 transition-opacity" onClick={onClose}></div>

        <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-10">
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} rounded-t-2xl`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}><i className="ri-settings-4-line" /></div>
              <h3 className={WMS_POPUP_TITLE}>Map Settings</h3>
            </div>
            <button onClick={onClose} className={WMS_POPUP_CLOSE}>
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Base Layer */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5 flex items-center gap-1.5">
                <i className="ri-stack-line text-teal-500"></i> Base Layer
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {BASE_LAYERS.map(layer => (
                  <button
                    key={layer.key}
                    onClick={() => update({ baseLayer: layer.key })}
                    className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all ${
                      localConfig.baseLayer === layer.key
                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-sm'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <i className={`${layer.preview} text-xl ${localConfig.baseLayer === layer.key ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500 dark:text-gray-400'}`}></i>
                    <span className={`text-[10px] font-medium ${localConfig.baseLayer === layer.key ? 'text-teal-700 dark:text-teal-300' : 'text-gray-600 dark:text-gray-400'}`}>
                      {layer.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Controls */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5 flex items-center gap-1.5">
                <i className="ri-toggle-line text-teal-500"></i> Controls
              </h4>
              <div className="space-y-2">
                <ToggleRow
                  label="Zoom Control"
                  description="Show +/- zoom buttons on map"
                  icon="ri-zoom-in-line"
                  checked={localConfig.zoomControl}
                  onChange={(v) => update({ zoomControl: v })}
                />
                <ToggleRow
                  label="Fullscreen"
                  description="Allow map to go fullscreen"
                  icon="ri-fullscreen-line"
                  checked={localConfig.fullscreenControl}
                  onChange={(v) => update({ fullscreenControl: v })}
                />
                <ToggleRow
                  label="Layers Control"
                  description="Show layers picker on map"
                  icon="ri-stack-line"
                  checked={localConfig.showLayersControl}
                  onChange={(v) => update({ showLayersControl: v })}
                />
                <ToggleRow
                  label="Map Labels"
                  description="Show labels directly on markers"
                  icon="ri-font-size-2"
                  checked={localConfig.showLabels}
                  onChange={(v) => update({ showLabels: v })}
                />
                <ToggleRow
                  label="Info on Hover"
                  description="Show details card when hovering"
                  icon="ri-cursor-line"
                  checked={localConfig.showInfoOnHover}
                  onChange={(v) => update({ showInfoOnHover: v })}
                />
              </div>
            </div>

            {/* Layers */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5 flex items-center gap-1.5">
                <i className="ri-stack-overflow-line text-teal-500"></i> Layers
              </h4>
              <div className="space-y-2">
                <ToggleRow
                  label="Heat Markers"
                  description="District/UDA data visualization"
                  icon="ri-fire-line"
                  checked={localConfig.showHeatMarkers}
                  onChange={(v) => update({ showHeatMarkers: v })}
                />
                {localConfig.showHeatMarkers && (
                  <div className="ml-1 pl-4 border-l-2 border-teal-100 dark:border-teal-800">
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mb-2">Heatmap Style</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => update({ heatmapStyle: 'bubbles' })}
                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                          localConfig.heatmapStyle === 'bubbles'
                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-sm'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="relative w-10 h-10 flex items-center justify-center">
                          <div className="absolute w-6 h-6 rounded-full bg-orange-400/60 border-2 border-orange-400"></div>
                          <div className="absolute top-0 right-0 w-4 h-4 rounded-full bg-green-400/60 border-2 border-green-400"></div>
                          <div className="absolute bottom-0 left-0.5 w-3 h-3 rounded-full bg-red-400/60 border-2 border-red-400"></div>
                        </div>
                        <span className={`text-[10px] font-medium ${localConfig.heatmapStyle === 'bubbles' ? 'text-teal-700 dark:text-teal-300' : 'text-gray-600 dark:text-gray-400'}`}>
                          Bubbles
                        </span>
                      </button>
                      <button
                        onClick={() => update({ heatmapStyle: 'fill' })}
                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                          localConfig.heatmapStyle === 'fill'
                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/30 shadow-sm'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        <div className="relative w-10 h-10 flex items-center justify-center">
                          <svg viewBox="0 0 40 40" className="w-10 h-10">
                            <path d="M5 10 L18 5 L25 12 L35 8 L38 20 L30 32 L15 35 L5 25Z" fill="#f9731680" stroke="#f97316" strokeWidth="1.5" />
                            <path d="M2 20 L10 15 L15 22 L8 30Z" fill="#22c55e80" stroke="#22c55e" strokeWidth="1.5" />
                          </svg>
                        </div>
                        <span className={`text-[10px] font-medium ${localConfig.heatmapStyle === 'fill' ? 'text-teal-700 dark:text-teal-300' : 'text-gray-600 dark:text-gray-400'}`}>
                          Fill Color
                        </span>
                      </button>
                    </div>
                  </div>
                )}
                <ToggleRow
                  label="KPI Badges"
                  description="Works count & value overlay cards"
                  icon="ri-price-tag-3-line"
                  checked={localConfig.showKpiBadges}
                  onChange={(v) => update({ showKpiBadges: v })}
                />
                <ToggleRow
                  label="Legend"
                  description="Heat intensity scale"
                  icon="ri-palette-line"
                  checked={localConfig.showLegend}
                  onChange={(v) => update({ showLegend: v })}
                />
              </div>
            </div>

            {/* Marker Opacity */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2.5 flex items-center gap-1.5">
                <i className="ri-contrast-2-line text-teal-500"></i> Marker Opacity
              </h4>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={20}
                  max={100}
                  step={5}
                  value={localConfig.markerOpacity}
                  onChange={(e) => update({ markerOpacity: Number(e.target.value) })}
                  className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full appearance-none cursor-pointer accent-teal-600"
                />
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 w-8 text-right">{localConfig.markerOpacity}%</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                const defaults = { ...DEFAULT_MAP_CONFIG };
                setLocalConfig(defaults);
                onChange(defaults);
              }}
              className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium flex items-center gap-1"
            >
              <i className="ri-refresh-line text-sm"></i> Reset Defaults
            </button>
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold rounded-lg text-white transition-all"
              style={{ backgroundColor: '#0d9488' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Toggle row sub-component
function ToggleRow({ label, description, icon, checked, onChange }: {
  label: string;
  description: string;
  icon: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div
      className="flex items-center justify-between p-2.5 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
      onClick={() => onChange(!checked)}
    >
      <div className="flex items-center gap-2.5">
        <i className={`${icon} text-base ${checked ? 'text-teal-600' : 'text-gray-400 dark:text-gray-500'}`}></i>
        <div>
          <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{label}</p>
          <p className="text-[10px] text-gray-400 dark:text-gray-500">{description}</p>
        </div>
      </div>
      <div
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? 'bg-teal-500' : 'bg-gray-300 dark:bg-gray-600'}`}
      >
        <div
          className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        ></div>
      </div>
    </div>
  );
}
